import { Alert, Button, Grid, Stack } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertCircle, IconRefresh } from "@tabler/icons-react";
import { useEffect } from "react";
import { useApiQuery } from "@/hooks/useApiQuery";
import { EMPTY_DEMOGRAFI } from "./demografi/demografi.types";
import { DemografiAgeCard } from "./demografi/demografi-age-card";
import { DemografiBanjarCard } from "./demografi/demografi-banjar-card";
import { DemografiDynamicsCard } from "./demografi/demografi-dynamics-card";
import { DemografiJobCard } from "./demografi/demografi-job-card";
import {
	ROW3_CARD_HEIGHT,
	ROW3_FIXED_HEIGHT_MIN_WIDTH,
} from "./demografi/demografi-layout.const";
import { DemografiReligionCard } from "./demografi/demografi-religion-card";
import { DemografiSectorCard } from "./demografi/demografi-sector-card";
import { DemografiSummaryCards } from "./demografi/demografi-summary-cards";
import { fetchDemografiAll } from "./demografi/fetch-demografi";

const DemografiPekerjaan = () => {
	const {
		data = EMPTY_DEMOGRAFI,
		isLoading: loading,
		isError,
		refetch,
	} = useApiQuery(["demografi", "all"], fetchDemografiAll, {
		autoRefresh: true,
	});
	const {
		stats,
		ageData,
		jobData,
		religionRows,
		banjarData,
		sektorData,
		births,
		deaths,
		moveIn,
		moveOut,
	} = data;
	// Tinggi Row 3 hanya dipatok di desktop; di bawah `lg` kartu menumpuk dan
	// tinggi alami lebih enak dibaca. Default `false` agar render pertama (sebelum
	// media query terbaca) memakai tinggi alami, bukan kotak scroll.
	const row3FixedHeight = useMediaQuery(
		`(min-width: ${ROW3_FIXED_HEIGHT_MIN_WIDTH})`,
		false,
	);
	const row3Height = row3FixedHeight ? ROW3_CARD_HEIGHT : undefined;
	const error = isError
		? "Gagal memuat data demografi. Periksa koneksi dan coba lagi."
		: null;

	// Listen for sync complete event to refresh data
	useEffect(() => {
		const handleSyncComplete = () => {
			refetch();
		};

		window.addEventListener("demografi-sync-complete", handleSyncComplete);
		return () => {
			window.removeEventListener("demografi-sync-complete", handleSyncComplete);
		};
	}, [refetch]);

	return (
		<Stack gap="lg">
			{error && (
				<Alert
					icon={<IconAlertCircle size={16} />}
					color="red"
					title="Gagal memuat data"
					radius="md"
				>
					{error}
					<Button
						data-ai-target="demografi.coba-lagi"
						size="xs"
						variant="light"
						color="red"
						leftSection={<IconRefresh size={14} />}
						onClick={() => refetch()}
						mt="xs"
					>
						Coba lagi
					</Button>
				</Alert>
			)}

			<DemografiSummaryCards stats={stats} births={births} loading={loading} />

			<Grid gutter="lg">
				<Grid.Col span={{ base: 12, lg: 4 }}>
					<DemografiAgeCard ageData={ageData} loading={loading} />
				</Grid.Col>
				<Grid.Col span={{ base: 12, lg: 4 }}>
					<DemografiJobCard jobData={jobData} loading={loading} />
				</Grid.Col>
				<Grid.Col span={{ base: 12, lg: 4 }}>
					<DemografiDynamicsCard
						births={births}
						deaths={deaths}
						moveIn={moveIn}
						moveOut={moveOut}
						loading={loading}
					/>
				</Grid.Col>
			</Grid>

			{/*
			  Tinggi dipatok mulai breakpoint `lg`: tanpa itu ketiga kolom mengikuti
			  kartu tertinggi (Data per Banjar) sehingga Sektor Unggulan menyisakan
			  ruang kosong besar. Di bawah `lg` kartu menumpuk dan tinggi alami lebih
			  enak dibaca, jadi patokan sengaja tidak diterapkan.
			*/}
			<Grid gutter="lg">
				<Grid.Col span={{ base: 12, lg: 4 }} h={row3Height}>
					<DemografiReligionCard
						religionRows={religionRows}
						loading={loading}
					/>
				</Grid.Col>
				<Grid.Col span={{ base: 12, lg: 4 }} h={row3Height}>
					<DemografiBanjarCard banjarData={banjarData} loading={loading} />
				</Grid.Col>
				<Grid.Col span={{ base: 12, lg: 4 }} h={row3Height}>
					<DemografiSectorCard sektorData={sektorData} loading={loading} />
				</Grid.Col>
			</Grid>
		</Stack>
	);
};

export default DemografiPekerjaan;
